import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Stethoscope, Plus, Trash2, Pencil, Phone, Mail, MapPin, Building2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const SPECIALTIES = ["GP / Family Doctor", "Physiotherapist", "Psychiatrist", "Psychologist",
  "Podiatrist", "Occupational Therapist", "Dentist", "Optometrist", "Specialist", "Other"];

export default function MedicalStaffPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const blank = { name: "", specialty: "", clinic: "", address: "", phone: "", email: "", notes: "" };
  const [form, setForm] = useState(blank);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = async () => {
    try { setRows((await api.get("/medical-staff")).data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEdit(null); setForm(blank); setOpen(true); };
  const startEdit = (row) => { setEdit(row); setForm({ ...blank, ...row }); setOpen(true); };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    try {
      const r = edit
        ? await api.put(`/medical-staff/${edit.id}`, form)
        : await api.post("/medical-staff", form);
      if (r.data?.pending_approval) toast("Sent to client for approval");
      else if (r.data?.status === "pending") toast("Saved — awaiting client approval");
      else toast.success(edit ? "Updated" : "Added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.name}?`)) return;
    try {
      const r = await api.delete(`/medical-staff/${row.id}`);
      if (r.data?.pending_approval) toast("Deletion sent to client for approval");
      else toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="medical-staff-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight flex items-center gap-3">
            <Stethoscope className="w-8 h-8 text-coral" /> Treating Medical Staff
          </h1>
          <p className="text-muted-foreground">
            All the doctors, allied health and specialists who treat {user?.role === "client" ? "you" : "your client"}.
            {" "}{user?.role === "worker" && <span className="italic">Edits go to the client for approval.</span>}
          </p>
        </div>
        <Button onClick={startNew} className="rounded-full font-bold gap-2 min-h-[48px]"
                style={{ background: "hsl(var(--coral))", color: "white" }}
                data-testid="add-medical-staff-btn">
          <Plus className="w-4 h-4" /> Add staff
        </Button>
      </header>

      <div className="grid sm:grid-cols-2 gap-4">
        {rows.map((r) => (
          <Card key={r.id} className="rounded-3xl p-5 border-2 card-lift" data-testid={`medical-staff-card-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-display font-extrabold text-xl truncate">{r.name}</h3>
                {r.specialty && (
                  <span className="inline-block mt-1 rounded-full bg-coral/15 text-coral text-[11px] uppercase tracking-wider font-bold px-2.5 py-0.5">
                    {r.specialty}
                  </span>
                )}
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" className="rounded-full" onClick={() => startEdit(r)} data-testid={`edit-medical-${r.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" className="rounded-full text-destructive" onClick={() => remove(r)} data-testid={`del-medical-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-1.5 mt-3 text-sm">
              {r.clinic && <Line icon={Building2}>{r.clinic}</Line>}
              {r.address && <Line icon={MapPin}>{r.address}</Line>}
              {r.phone && <Line icon={Phone}><a href={`tel:${r.phone}`} className="hover:underline">{r.phone}</a></Line>}
              {r.email && <Line icon={Mail}><a href={`mailto:${r.email}`} className="hover:underline truncate">{r.email}</a></Line>}
            </div>
            {r.notes && <p className="text-xs text-muted-foreground mt-3 border-t pt-2 line-clamp-3">{r.notes}</p>}
          </Card>
        ))}
        {rows.length === 0 && (
          <Card className="rounded-3xl p-10 border-2 border-dashed text-center text-muted-foreground sm:col-span-2">
            No treating medical staff added yet.
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="medical-staff-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{edit ? "Edit treating staff" : "Add treating staff"}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Fill in name and specialty. The client must approve worker-submitted entries.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><F id="ms-name" label="Name *" v={form.name} oc={upd("name")} placeholder="e.g. Dr Helen Choi" /></div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Specialty</Label>
              <select value={form.specialty} onChange={upd("specialty")}
                      className="w-full rounded-xl border-2 border-input bg-background min-h-[48px] px-3 text-sm font-semibold"
                      data-testid="field-ms-specialty">
                <option value="">Choose…</option>
                {SPECIALTIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <F id="ms-clinic" label="Clinic" v={form.clinic} oc={upd("clinic")} />
            <F id="ms-phone" label="Phone" v={form.phone} oc={upd("phone")} />
            <div className="col-span-2"><F id="ms-address" label="Address" v={form.address} oc={upd("address")} /></div>
            <div className="col-span-2"><F id="ms-email" label="Email" v={form.email} oc={upd("email")} type="email" /></div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={upd("notes")} className="rounded-xl" data-testid="field-ms-notes" placeholder="Conditions managed, special instructions…" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-full font-bold" style={{ background: "hsl(var(--coral))", color: "white" }} data-testid="save-medical-staff-btn">
              {edit ? "Save changes" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Line({ icon: Icon, children }) {
  return <div className="flex items-center gap-2 text-foreground/80"><Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />{children}</div>;
}
function F({ id, label, v, oc, type = "text", placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase font-bold tracking-wider">{label}</Label>
      <Input value={v} onChange={oc} type={type} placeholder={placeholder} className="rounded-xl min-h-[48px]" data-testid={`field-${id}`} />
    </div>
  );
}
